import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Always required — every form control must have a visible, associated label (APP_SPEC §36). */
  label: string;
  /** Field-level validation error message, shown below the input and announced via `aria-describedby`. */
  error?: string;
  /** Supplementary helper text, shown when there is no error. */
  hint?: string;
}

/**
 * A labeled text input with accessible error/hint wiring (TECH_STACK §16;
 * APP_SPEC §36: "appropriate form labels"). Forwards its ref so React Hook
 * Form's `register()` can attach directly to the underlying `<input>`.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, id, className = '', ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-semibold text-textPrimary">
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={`min-h-touch min-w-0 rounded-md border bg-parchment px-3 py-2 text-base text-textPrimary shadow-inset transition-colors duration-fast placeholder:text-textMuted hover:border-borderStrong focus:border-accent focus:outline-none disabled:cursor-not-allowed disabled:bg-inset disabled:text-textMuted ${error ? 'border-danger' : 'border-border'} ${className}`}
        {...rest}
      />
      {error ? (
        <p id={errorId} role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-textSecondary">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
